import type { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import type { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import {
	BulkClaimGenerationJobStatus,
	type BulkClaimGenerationJobStatusRes,
} from '@dealstack/shared/lib/backendClient';
import type { InvestorKey } from './BulkClaimOnBehalf.types';

/** Percentage (0–100) of investors attempted (generated + failed) out of the total. */
export const generationProgressPercent = (state: BulkClaimGenerationJobStatusRes): number => {
	if (state.total === 0) {
		return 0;
	}

	return Math.min(100, Math.round(((state.generated + state.failed) / state.total) * 100));
};

/** True once the job has finished (successfully or not) — polling/progress can stop. */
export const isBulkClaimGenerationJobTerminal = (status: BulkClaimGenerationJobStatus): boolean =>
	status === BulkClaimGenerationJobStatus.COMPLETED ||
	status === BulkClaimGenerationJobStatus.FAILED;

export const investorKey = (roleType: DocumentSigningRoleType, investorId: string): InvestorKey =>
	`${roleType}:${investorId}`;

const ROLE_TYPE_LABEL_KEYS: Record<DocumentSigningRoleType, string> = {
	PARTY: 'documents_page.claim_on_behalf.role.investor',
	REPRESENTATIVE: 'documents_page.claim_on_behalf.role.representative',
	PIV: 'documents_page.claim_on_behalf.role.piv',
	COUNTERPARTY: 'documents_page.claim_on_behalf.role.counterparty',
	WITNESS: 'documents_page.claim_on_behalf.role.witness',
	CERTIFIER: 'documents_page.claim_on_behalf.role.certifier',
};

export const roleTypeLabel = (
	roleType: DocumentSigningRoleType,
	tMipManage: TranslateFunction<'mip-manage'>
): string => tMipManage(ROLE_TYPE_LABEL_KEYS[roleType]);

// Two-letter avatar initials from a resolved signer/entity name, e.g. "jon doe LTD" -> "JL",
// "a rep a" -> "AA", "Alice" -> "AL". Uses first + last word so multi-word entity names read well.
export const nameInitials = (name: string): string => {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) {
		return '';
	}
	if (parts.length === 1) {
		return parts[0].slice(0, 2).toUpperCase();
	}
	return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};
